// src/pages/ReviewsPage.jsx
import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../services/firebase";
import Navigation from "../components/Common/header/Navigation";
import Footer from "../components/Common/footer/Footer";
import "../styles/Pages.css";

export default function ReviewsPage() {
    const [reviews, setReviews]           = useState([]);
    const [loading, setLoading]           = useState(true);
    const [showForm, setShowForm]         = useState(false);
    const [submitMessage, setSubmitMessage] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const [formData, setFormData] = useState({
        customerName: "",
        customerEmail: "",
        rating: 5,
        content: "",
        hairType: "",
        productType: "",
        anonymous: false,
    });

    useEffect(() => {
        const fetchApprovedReviews = async () => {
            try {
                const q = query(collection(db, "reviews"), where("status", "==", "approved"));
                const snapshot = await getDocs(q);
                const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                data.sort((a, b) => {
                    const dA = a.createdAt ? a.createdAt.toDate() : 0;
                    const dB = b.createdAt ? b.createdAt.toDate() : 0;
                    return dB - dA;
                });
                setReviews(data);
            } catch (err) {
                console.error("Error fetching reviews:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchApprovedReviews();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitMessage("");
        try {
            await addDoc(collection(db, "reviews"), {
                customerName:  formData.anonymous ? "Anonymous" : formData.customerName,
                customerEmail: formData.customerEmail,
                rating:        formData.rating,
                content:       formData.content,
                hairType:      formData.hairType,
                productType:   formData.productType,
                anonymous:     formData.anonymous,
                status:        "pending",
                createdAt:     new Date(),
                source:        "website",
            });
            setSubmitSuccess(true);
            setSubmitMessage("Thank you! Your review has been submitted and is pending approval.");
            setShowForm(false);
            setFormData({ customerName: "", customerEmail: "", rating: 5, content: "", hairType: "", productType: "", anonymous: false });
        } catch (err) {
            console.error("Error submitting review:", err);
            setSubmitMessage("Something went wrong. Please try again.");
        }
    };

    return (
        <div className="page-shell">
            <Navigation />

            {/* ── Hero ── */}
            <section className="page-hero">
                <div className="page-hero-border" />
                <div className="container" style={{ position: "relative", zIndex: 1 }}>
                    <span className="page-hero-eyebrow">What our clients say</span>
                    <h1 className="page-hero-title">Client Experiences</h1>
                    <p className="page-hero-subtitle">
                        Discover what our clients are saying about Bundle Madness
                    </p>
                    <div className="page-hero-rule" />
                    <div style={{ marginTop: "32px" }}>
                        <button
                            className="btn-luxury"
                            onClick={() => setShowForm(!showForm)}
                        >
                            <span>{showForm ? "Cancel" : "Leave a Review"}</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Submit message ── */}
            {submitMessage && (
                <div className="container" style={{ paddingTop: "24px" }}>
                    <div className={submitSuccess ? "form-submit-message" : "form-error"}>
                        {submitMessage}
                    </div>
                </div>
            )}

            {/* ── Review form ── */}
            {showForm && (
                <section className="page-section">
                    <div className="container">
                        <div className="form-panel">
                            <h3 className="form-panel-title">Share Your Experience</h3>

                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="field-group">
                                            <label className="field-label">Name *</label>
                                            <input
                                                type="text"
                                                className="field-input"
                                                required
                                                disabled={formData.anonymous}
                                                value={formData.customerName}
                                                onChange={e => setFormData({ ...formData, customerName: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="field-group">
                                            <label className="field-label">Email (kept private)</label>
                                            <input
                                                type="email"
                                                className="field-input"
                                                value={formData.customerEmail}
                                                onChange={e => setFormData({ ...formData, customerEmail: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="field-group">
                                            <label className="field-label">Rating *</label>
                                            <div className="star-rating">
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <button
                                                        key={star}
                                                        type="button"
                                                        className={`star ${star <= formData.rating ? "active" : ""}`}
                                                        onClick={() => setFormData({ ...formData, rating: star })}
                                                    >
                                                        ★
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="field-group">
                                            <label className="field-label">Hair Type</label>
                                            <select
                                                className="field-select"
                                                value={formData.hairType}
                                                onChange={e => setFormData({ ...formData, hairType: e.target.value })}
                                            >
                                                <option value="">Select your hair type</option>
                                                <option value="Straight">Straight</option>
                                                <option value="Wavy">Wavy</option>
                                                <option value="Curly">Curly</option>
                                                <option value="Coily">Coily</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="field-group">
                                    <label className="field-label">Your Review *</label>
                                    <textarea
                                        className="field-textarea"
                                        required
                                        rows="4"
                                        value={formData.content}
                                        onChange={e => setFormData({ ...formData, content: e.target.value })}
                                        placeholder="Tell us about your experience..."
                                    />
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
                                    <input
                                        type="checkbox"
                                        id="anon"
                                        style={{ accentColor: "var(--gold)", width: "16px", height: "16px", cursor: "pointer" }}
                                        checked={formData.anonymous}
                                        onChange={e => setFormData({ ...formData, anonymous: e.target.checked })}
                                    />
                                    <label htmlFor="anon" style={{ fontFamily: "var(--font-body)", fontSize: "0.75rem", letterSpacing: "1px", color: "var(--text-secondary)", cursor: "pointer" }}>
                                        Post my review anonymously
                                    </label>
                                </div>

                                <button type="submit" className="btn-luxury" style={{ width: "100%" }}>
                                    <span>Submit Review</span>
                                </button>
                            </form>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Reviews grid ── */}
            <section className="page-section">
                <div className="container">
                    {loading ? (
                        <div className="page-spinner">
                            <div className="page-spinner-ring" />
                            <span className="page-spinner-text">Loading reviews</span>
                        </div>
                    ) : reviews.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "60px 0" }}>
                            <p style={{ fontFamily: "var(--font-body)", fontSize: "0.72rem", letterSpacing: "3px", textTransform: "uppercase", color: "var(--text-muted)" }}>
                                No reviews yet — be the first to share your experience
                            </p>
                        </div>
                    ) : (
                        <div className="review-grid">
                            {reviews.map(review => (
                                <div key={review.id} className="review-card">
                                    <div className="review-card-stars">
                                        {"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}
                                    </div>
                                    <p className="review-card-text">"{review.content}"</p>
                                    <div className="review-card-footer">
                                        <span className="review-card-name">{review.customerName}</span>
                                        {review.hairType && (
                                            <span className="review-card-meta"> · {review.hairType} Hair</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
}